import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import './ticketDetail.css';
import {
  getTicketById,
  deleteTicket,
  assignTicket,
  updateTicket,
  getComments,
  createComment,
  deleteComment,
  getAttachments,
  uploadAttachment,
  deleteAttachment,
  getAdminUsers,
} from './apiTickets';
import useTicketWebSocket from './useTicketWebSocket';
import BASE_URL from '../../config';

const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);

  // WebSocket for real-time updates on this specific ticket
  useTicketWebSocket({
    onTicketUpdated: (updatedTicket) => {
      if (updatedTicket.id === parseInt(id)) {
        console.log('Ticket updated via WebSocket:', updatedTicket);
        setTicket(prev => ({ ...prev, ...updatedTicket }));
        toast.info('Ticket was updated');
      }
    },
    onTicketDeleted: (data) => {
      const ticketId = data.ticket_id || data.id;
      if (ticketId === parseInt(id)) {
        toast.warning('This ticket was deleted');
        navigate('/user-dashboard/tickets');
      }
    },
    onCommentAdded: (comment) => {
      if (comment.ticket === parseInt(id)) {
        console.log('Comment added via WebSocket:', comment);
        setComments(prev => [...prev, comment]);
        toast.info('New comment added');
      }
    },
    onCommentDeleted: (data) => {
      if (data.ticket === parseInt(id)) {
        const commentId = data.comment_id || data.id;
        console.log('Comment deleted via WebSocket:', commentId);
        setComments(prev => prev.filter(c => c.id !== commentId));
        toast.info('Comment was deleted');
      }
    },
    onAttachmentAdded: (attachment) => {
      if (attachment.ticket === parseInt(id)) {
        console.log('Attachment added via WebSocket:', attachment);
        setAttachments(prev => [...prev, attachment]);
        toast.info('New attachment added');
      }
    },
    onAttachmentDeleted: (data) => {
      if (data.ticket === parseInt(id)) {
        const attachmentId = data.attachment_id || data.id;
        console.log('Attachment deleted via WebSocket:', attachmentId);
        setAttachments(prev => prev.filter(a => a.id !== attachmentId));
        toast.info('Attachment was deleted');
      }
    },
  });

  // Check if user is admin and get user ID
  useEffect(() => {
    fetch(`${BASE_URL}/api/user-profile/`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setIsAdmin(data.is_admin);
        setCurrentUserId(data.id);

        // If admin, fetch list of admin users for assignment
        if (data.is_admin) {
          loadAdminUsers();
        }
      })
      .catch((err) => {
        console.error('Error fetching user data:', err);
      });
  }, []);

  const loadAdminUsers = async () => {
    try {
      const users = await getAdminUsers();
      setAdminUsers(users);
    } catch (error) {
      console.error('Error fetching admin users:', error);
    }
  };

  // Fetch ticket details, comments, and attachments
  useEffect(() => {
    if (id) {
      loadTicketData();
    }
  }, [id]);

  const loadTicketData = async () => {
    try {
      setIsLoading(true);
      const [ticketData, commentsData, attachmentsData] = await Promise.all([
        getTicketById(id),
        getComments(id),
        getAttachments(id),
      ]);

      setTicket(ticketData);
      setComments(commentsData);
      setAttachments(attachmentsData);
    } catch (error) {
      console.error('Error loading ticket data:', error);
      if (error.response && error.response.status === 403) {
        toast.error('You do not have permission to view this ticket');
        navigate('/user-dashboard/tickets');
      } else if (error.response && error.response.status === 404) {
        toast.error('Ticket not found');
        navigate('/user-dashboard/tickets');
      } else {
        toast.error('Failed to load ticket details');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/user-dashboard/tickets');
  };

  const handleDelete = async () => {
    if (!isAdmin && ticket.status !== 'OPEN') {
      toast.error('You can only delete tickets with OPEN status');
      return;
    }

    if (window.confirm('Are you sure you want to delete this ticket?')) {
      try {
        await deleteTicket(id);
        toast.success('Ticket deleted successfully');
        navigate('/user-dashboard/tickets');
      } catch (error) {
        console.error('Error deleting ticket:', error);
        toast.error('Failed to delete ticket');
      }
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const updatedTicket = await updateTicket(id, { status: newStatus });
      setTicket(updatedTicket);
      toast.success('Status updated successfully');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleAssignmentChange = async (userId) => {
    try {
      const updatedTicket = await assignTicket(id, userId === '' ? null : parseInt(userId));
      setTicket(updatedTicket);
      toast.success('Assignment updated successfully');
    } catch (error) {
      console.error('Error updating assignment:', error);
      toast.error('Failed to update assignment');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();

    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    if (ticket.status === 'CLOSED' && !isAdmin) {
      toast.error('Cannot add comments to closed tickets');
      return;
    }

    try {
      setIsSubmittingComment(true);
      const createdComment = await createComment(id, newComment);
      setComments([...comments, createdComment]);
      setNewComment('');
      toast.success('Comment added successfully');
    } catch (error) {
      console.error('Error adding comment:', error);
      if (error.response && error.response.data && error.response.data.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to add comment');
      }
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      try {
        await deleteComment(id, commentId);
        setComments(comments.filter((c) => c.id !== commentId));
        toast.success('Comment deleted successfully');
      } catch (error) {
        console.error('Error deleting comment:', error);
        toast.error('Failed to delete comment');
      }
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (10 MB limit)
    const maxSize = 10 * 1024 * 1024; // 10 MB in bytes
    if (file.size > maxSize) {
      toast.error('File size exceeds 10 MB limit');
      return;
    }

    try {
      setIsUploadingFile(true);
      const uploadedAttachment = await uploadAttachment(id, file);
      setAttachments([...attachments, uploadedAttachment]);
      toast.success('File uploaded successfully');
      // Reset file input
      e.target.value = '';
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (window.confirm('Are you sure you want to delete this attachment?')) {
      try {
        await deleteAttachment(id, attachmentId);
        setAttachments(attachments.filter((a) => a.id !== attachmentId));
        toast.success('Attachment deleted successfully');
      } catch (error) {
        console.error('Error deleting attachment:', error);
        toast.error('Failed to delete attachment');
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'IMAGE':
        return '🖼️';
      case 'DOCUMENT':
        return '📄';
      default:
        return '📎';
    }
  };

  if (isLoading) {
    return (
      <div className="ticket-detail-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading ticket details...</p>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="ticket-detail-container">
        <div className="empty-state">
          <h2 className="empty-state-title">Ticket not found</h2>
          <button className="btn-primary" onClick={handleBack}>
            Back to Tickets
          </button>
        </div>
      </div>
    );
  }

  const canDelete = isAdmin || ticket.status === 'OPEN';
  const canEdit = isAdmin || ticket.created_by === currentUserId;

  const handleEdit = () => {
    navigate(`/user-dashboard/tickets/${id}/edit`);
  };

  return (
    <div className="ticket-detail-container">
      {/* Header */}
      <div className="ticket-detail-header">
        <div className="ticket-detail-title-section">
          <button className="back-button" onClick={handleBack}>
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            Back to Tickets
          </button>
          <div className="ticket-detail-id">Ticket No: {ticket.id}</div>
          <h1 className="ticket-detail-title">{ticket.title}</h1>
        </div>
        <div className="ticket-detail-actions">
          {canEdit && (
            <button className="btn-secondary" onClick={handleEdit}>
              Edit Ticket
            </button>
          )}
          {canDelete && (
            <button className="btn-danger" onClick={handleDelete}>
              Delete Ticket
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="ticket-detail-content">
        {/* Left Column */}
        <div className="ticket-detail-main">
          {/* Description */}
          <div className="detail-section">
            <h2 className="section-title">Description</h2>
            <p className="description-text">{ticket.description}</p>
          </div>

          {/* Comments */}
          <div className="detail-section">
            <h2 className="section-title">
              Comments ({comments.length})
            </h2>

            {comments.length === 0 ? (
              <p className="empty-comments">No comments yet. Be the first to comment!</p>
            ) : (
              <div className="comments-list">
                {comments.map((comment) => (
                  <div key={comment.id} className="comment-item">
                    <div className="comment-header">
                      <span className="comment-author">{comment.user_username || 'User'}</span>
                      <span className="comment-date">{formatDate(comment.created_at)}</span>
                    </div>
                    <p className="comment-text">{comment.comment}</p>
                    {(isAdmin || comment.user === currentUserId) && (
                      <div className="comment-actions">
                        <button
                          className="btn-delete-comment"
                          onClick={() => handleDeleteComment(comment.id)}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Add Comment Form */}
            {(ticket.status !== 'CLOSED' || isAdmin) && (
              <form className="comment-form" onSubmit={handleAddComment}>
                <textarea
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  disabled={isSubmittingComment}
                />
                <div className="comment-form-actions">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isSubmittingComment}
                  >
                    {isSubmittingComment ? 'Posting...' : 'Post Comment'}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Attachments */}
          <div className="detail-section">
            <h2 className="section-title">
              <AttachFileIcon sx={{ fontSize: 24 }} />
              Attachments ({attachments.length})
            </h2>

            {attachments.length === 0 ? (
              <p className="empty-attachments">No attachments yet.</p>
            ) : (
              <div className="attachments-grid">
                {attachments.map((attachment) => (
                  <div key={attachment.id} className="attachment-item">
                    <div className="attachment-icon">{getFileIcon(attachment.file_type)}</div>
                    <div className="attachment-name" title={attachment.file_name}>
                      {attachment.file_name}
                    </div>
                    <div className="attachment-size">{formatFileSize(attachment.file_size)}</div>
                    <div className="attachment-actions">
                      <a
                        href={attachment.file}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-view"
                      >
                        View
                      </a>
                      {(isAdmin || attachment.uploaded_by === currentUserId) && (
                        <button
                          className="btn-delete"
                          onClick={() => handleDeleteAttachment(attachment.id)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Upload Section */}
            <div className="upload-section">
              <label htmlFor="file-upload" className="file-upload-area">
                <input
                  id="file-upload"
                  type="file"
                  onChange={handleFileUpload}
                  disabled={isUploadingFile}
                />
                <div className="upload-icon">
                  <CloudUploadIcon sx={{ fontSize: 48 }} />
                </div>
                <p className="upload-text">
                  {isUploadingFile ? 'Uploading...' : 'Click to upload or drag and drop'}
                </p>
                <p className="upload-hint">Maximum file size: 10 MB</p>
              </label>
            </div>
          </div>
        </div>

        {/* Right Column (Sidebar) */}
        <div className="ticket-detail-sidebar">
          {/* Ticket Info */}
          <div className="info-section">
            <h3 className="info-title">Ticket Information</h3>

            <div className="info-item">
              <div className="info-label">Status</div>
              <div className="info-value">
                <span className="status-badge" data-status={ticket.status}>
                  {ticket.status === 'IN_PROGRESS' ? 'In Progress' : ticket.status}
                </span>
              </div>
            </div>

            <div className="info-item">
              <div className="info-label">Priority</div>
              <div className="info-value">
                <span className="priority-badge" data-priority={ticket.priority}>
                  {ticket.priority}
                </span>
              </div>
            </div>

            <div className="info-item">
              <div className="info-label">Issue Type</div>
              <div className="info-value">
                {ticket.issue_type === 'GENERAL_QUESTION' ? 'General Question' :
                 ticket.issue_type === 'FEATURE_REQUEST' ? 'Feature Request' :
                 ticket.issue_type ? ticket.issue_type.charAt(0) + ticket.issue_type.slice(1).toLowerCase() : 'Other'}
              </div>
            </div>

            <div className="info-item">
              <div className="info-label">Created By</div>
              <div className="info-value">{ticket.created_by_username || 'User'}</div>
            </div>

            <div className="info-item">
              <div className="info-label">Created At</div>
              <div className="info-value">{formatDate(ticket.created_at)}</div>
            </div>

            <div className="info-item">
              <div className="info-label">Last Updated</div>
              <div className="info-value">{formatDate(ticket.updated_at)}</div>
            </div>

            {ticket.assigned_to && (
              <div className="info-item">
                <div className="info-label">Assigned To</div>
                <div className="info-value">{ticket.assigned_to_username || 'Admin'}</div>
              </div>
            )}

            {/* Admin Controls */}
            {isAdmin && (
              <div className="admin-controls">
                <select
                  value={ticket.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                >
                  <option value="OPEN">Open</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="CLOSED">Closed</option>
                </select>

                <select
                  value={ticket.assigned_to || ''}
                  onChange={(e) => handleAssignmentChange(e.target.value)}
                >
                  <option value="">Unassigned</option>
                  {adminUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.display_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDetail;
